/** Read-only commentator view. HTTP and WS force the announcer projection even for administrators. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ScorioSocket } from "@/api/ws";
import { RoleDenied, RoleRoute, type RolePageContext } from "@/auth/RoleRoute";
import { useOnline } from "@/hooks/useOnline";
import { estimateClockSkew, useHeatTimer, type HeatTimerEvent } from "@/hooks/useHeatTimer";
import "./Announcer.css";
interface AnnouncerRider {
  heat_rider_id: string;
  name?: string;
  bib?: string | null;
  final: string | number | null;
  placement: number | null;
  status_label?: string | null;
  attempts: {
    attempt_number: number;
    total?: string | number | null;
    judge_scores?: Record<string, string | number>;
  }[];
}
interface Need {
  value: string | null;
  possible: boolean;
  reason: string | null;
}
interface AdjacentHeat {
  heat_id: string;
  name: string;
  results: { competitors: AnnouncerRider[] };
}
const needMessage = (need: Need | undefined, target: string) => {
  if (need?.value != null) return `Needs ${need.value} to ${target}`;
  if (need?.reason === "ordinal_or_population_scoring")
    return "A numeric score is unavailable for this format.";
  if (need?.reason === "above_available_attempt_scale")
    return "Beyond the available attempt score.";
  if (need?.reason === "heat_finished") return "Heat finished.";
  if (need?.reason === "no_open_attempt") return "No open attempt.";
  if (need?.reason === "competitor_unavailable") return "No numeric score for this rider status.";
  return target === "advance"
    ? "No numeric advancement cut available."
    : "Waiting for a visible leading score.";
};
interface AnnouncerState {
  areas?: {
    enabled: boolean;
    selected_id: string | null;
    choices: { id: string | null; name: string }[];
  };
  competition: { id: string; name: string; slug: string | null; timezone: string };
  heats: { id: string; name: string; status: string }[];
  heat_id: string | null;
  live: { focus_heat_rider_id?: string | null } | null;
  results: { competitors: AnnouncerRider[] } | null;
  needs?: {
    cut_line: number | null;
    needs: { heat_rider_id: string; needs_to_lead: Need; needs_to_advance: Need }[];
  };
  next?: AdjacentHeat | null;
  previous?: AdjacentHeat | null;
  standings?: {
    heat_rider_id: string;
    division_id?: string;
    name?: string;
    position: number | null;
    final: string | null;
    label?: string;
  }[];
  timer: HeatTimerEvent | null;
  notes: { id: string; heat_id: string | null; text: string; created_at: string }[];
}
export function AnnouncerRoute() {
  return (
    <RoleRoute roles={["announcer"]} watchSession={false}>
      {(context) => <AnnouncerPage key={context.principalKey} context={context} />}
    </RoleRoute>
  );
}
function AnnouncerPage({ context }: { context: RolePageContext }) {
  const { getToken, onSessionInvalid } = context;
  const { slug = "" } = useParams();
  const [state, setState] = useState<AnnouncerState | null>(null);
  const [area, setArea] = useState("");
  const areaQuery = area ? `?area_id=${encodeURIComponent(area)}` : "";
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [connected, setConnected] = useState(false);
  const [offset, setOffset] = useState(0);
  const online = useOnline();
  const alive = useRef(true);
  const sequence = useRef(0);
  const clock = useHeatTimer(state?.timer ?? null, state?.timer?.running ?? false, offset);
  const allowedSlug =
    !context.session || slug === (context.session.competitionSlug ?? context.session.competitionId);
  const load = useCallback(async () => {
    if (!navigator.onLine || !allowedSlug) return;
    const current = ++sequence.current;
    const sent = Date.now();
    try {
      const value = await context.transport.get<AnnouncerState>(
        `/api/announcer/${encodeURIComponent(slug)}${areaQuery}`,
      );
      if (!value?.competition?.id || !Array.isArray(value.heats) || !Array.isArray(value.notes))
        throw new Error("Invalid announcer response");
      if (alive.current && current === sequence.current) {
        if (value.timer)
          setOffset(estimateClockSkew(value.timer.server_time, sent, Date.now()).offsetMs);
        setState(value);
        setError("");
      }
    } catch (cause) {
      if (alive.current && current === sequence.current)
        setError(cause instanceof Error ? cause.message : "Could not load results");
    }
  }, [context.transport, slug, allowedSlug, areaQuery]);
  const invalidate = useCallback(() => {
    alive.current = false;
    sequence.current++;
  }, []);
  useEffect(() => {
    alive.current = true;
    setState(null);
    setError("");
    void load();
    return invalidate;
  }, [load, invalidate]);
  useEffect(() => {
    if (online) void load();
  }, [online, load]);
  const downloadStandings = async () => {
    setExporting(true);
    setExportError("");
    try {
      const blob = await context.transport.download(
        `/api/announcer/${encodeURIComponent(slug)}/standings.csv${areaQuery}`,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "announcer-standings.csv";
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (cause) {
      setExportError(cause instanceof Error ? cause.message : "Could not export standings");
    } finally {
      setExporting(false);
    }
  };
  const cid = state?.competition.id;
  useEffect(() => {
    if (!cid || !allowedSlug) return;
    const origin =
      (import.meta.env.VITE_WS_URL ??
        (import.meta.env.VITE_API_URL ?? "").replace(/^http/, "ws")) ||
      `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}`;
    const socket = new ScorioSocket(
      `${origin}/api/competitions/${encodeURIComponent(cid)}/announcer/ws${areaQuery}`,
      { getToken },
    );
    const offStatus = socket.onStatus(setConnected);
    const offClose = socket.onClose((code) => {
      if (code === 4401 || code === 4412) onSessionInvalid();
      else if (code === 4409) void load();
    });
    const offMessage = socket.subscribe((event) => {
      if (
        [
          "scores_update",
          "results_update",
          "live_state",
          "heats_update",
          "timer",
          "announcer_note",
          "needs_update",
          "posting_update",
        ].includes(event.type)
      )
        void load();
    });
    socket.connect();
    return () => {
      offStatus();
      offClose();
      offMessage();
      socket.disconnect();
    };
  }, [cid, allowedSlug, getToken, onSessionInvalid, load, areaQuery]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!connected && navigator.onLine && document.visibilityState === "visible") void load();
    }, 5000);
    return () => clearInterval(timer);
  }, [connected, load]);
  if (!allowedSlug) return <RoleDenied />;
  const active = state?.heats.find((heat) => heat.id === state.heat_id);
  const riders = state?.results?.competitors ?? [];
  const focus = riders.find((rider) => rider.heat_rider_id === state?.live?.focus_heat_rider_id);
  const notes =
    state?.notes.filter((note) => note.heat_id === null || note.heat_id === state.heat_id) ?? [];
  return (
    <main className="announcer-page">
      <header className="announcer-header">
        <strong>Scorio</strong>
        <span>{state?.competition.name ?? context.session?.competitionName ?? "Announcer"}</span>
        <button onClick={() => void context.onSignOut()}>
          {context.session ? "Leave session" : "Sign out"}
        </button>
      </header>
      <div className="announcer-content">
        <p className="announcer-eyebrow">Announcer · read only</p>
        {state?.areas?.enabled && (
          <details className="announcer-area">
            <summary>
              Area ·{" "}
              {state.areas.choices.find((choice) => choice.id === state.areas?.selected_id)?.name ??
                "Main course"}
            </summary>
            <label>
              Viewing area
              <select
                value={state.areas.selected_id ?? ""}
                disabled={!online || state.areas.choices.length < 2}
                onChange={(event) => setArea(event.target.value)}
              >
                {state.areas.choices.map((choice) => (
                  <option key={choice.id ?? "main"} value={choice.id ?? ""}>
                    {choice.name}
                  </option>
                ))}
              </select>
            </label>
          </details>
        )}
        {!online && (
          <p className="announcer-notice" role="status">
            Offline — showing last loaded results. Reconnect to receive updates.
          </p>
        )}
        {error && (
          <div className="announcer-error" role="alert">
            <h1>Could not load results</h1>
            <p>{error}</p>
            <button onClick={() => void load()} disabled={!online}>
              Retry
            </button>
          </div>
        )}
        {!state && !error && (
          <>
            <h1>Loading results</h1>
            <div
              className="announcer-skeleton"
              role="status"
              aria-label="Loading announcer results"
            />
          </>
        )}
        {state && !active && (
          <section className="announcer-empty">
            <h1>No heat started</h1>
            <p>Released results will appear here when the event starts.</p>
          </section>
        )}
        {state && active && (
          <>
            <div className="announcer-heat">
              <div>
                <p className="announcer-eyebrow">
                  {active.status === "live" ? "Live heat" : "Current heat"}
                </p>
                <h1>{active.name}</h1>
              </div>
              {state.timer && (
                <div className="announcer-clock">
                  <span>Time remaining</span>
                  <strong>{clock.display}</strong>
                </div>
              )}
            </div>
            {focus && (
              <section className="announcer-focus">
                <p className="announcer-eyebrow">On course</p>
                <h2>{focus.name ?? "Rider"}</h2>
                {focus.bib && <p>Bib {focus.bib}</p>}
                <p className="announcer-needs">
                  {needMessage(
                    state.needs?.needs.find((n) => n.heat_rider_id === focus.heat_rider_id)
                      ?.needs_to_lead,
                    "lead",
                  )}
                </p>
                <p>
                  {needMessage(
                    state.needs?.needs.find((n) => n.heat_rider_id === focus.heat_rider_id)
                      ?.needs_to_advance,
                    "advance",
                  )}
                </p>
              </section>
            )}
            <section className="announcer-results">
              <h2>Released results</h2>
              {state.needs?.cut_line != null && <p>Advancement cut: top {state.needs.cut_line}</p>}
              {riders.length === 0 ? (
                <p>No released results yet.</p>
              ) : (
                <div
                  className="announcer-table-region"
                  role="region"
                  aria-label="Released results"
                  tabIndex={0}
                >
                  <table>
                    <caption>Released heat standings</caption>
                    <thead>
                      <tr>
                        <th scope="col">Place</th>
                        <th scope="col">Rider</th>
                        <th scope="col">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {riders.map((rider) => (
                        <tr
                          key={rider.heat_rider_id}
                          className={
                            rider.heat_rider_id === focus?.heat_rider_id
                              ? "announcer-focused"
                              : undefined
                          }
                        >
                          <td>{rider.placement ?? "—"}</td>
                          <th scope="row">
                            {rider.name ?? "Rider"}
                            {rider.status_label && <small>{rider.status_label}</small>}
                            {state.needs && (
                              <>
                                <small>
                                  {needMessage(
                                    state.needs.needs.find(
                                      (n) => n.heat_rider_id === rider.heat_rider_id,
                                    )?.needs_to_lead,
                                    "lead",
                                  )}
                                </small>
                                <small>
                                  {needMessage(
                                    state.needs.needs.find(
                                      (n) => n.heat_rider_id === rider.heat_rider_id,
                                    )?.needs_to_advance,
                                    "advance",
                                  )}
                                </small>
                              </>
                            )}
                            {rider.attempts.some((attempt) =>
                              Object.keys(attempt.judge_scores ?? {}).some((key) =>
                                /^j[1-9]\d*$/.test(key),
                              ),
                            ) && (
                              <details>
                                <summary>Anonymous panel entries</summary>
                                {rider.attempts.map((attempt) => (
                                  <div key={attempt.attempt_number}>
                                    Attempt {attempt.attempt_number}:{" "}
                                    {Object.entries(attempt.judge_scores ?? {})
                                      .filter(([key]) => /^j[1-9]\d*$/.test(key))
                                      .map(([key, value]) => (
                                        <span key={key}>
                                          {key} {value}{" "}
                                        </span>
                                      ))}
                                  </div>
                                ))}
                              </details>
                            )}
                          </th>
                          <td>{rider.final ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            {(state.next || state.previous) && (
              <div className="announcer-adjacent">
                {(["next", "previous"] as const).map(
                  (key) =>
                    state[key] && (
                      <section className="announcer-results" key={key}>
                        <p className="announcer-eyebrow">
                          {key === "next" ? "Next up" : "Previous heat"}
                        </p>
                        <h2>{state[key].name}</h2>
                        <ol>
                          {state[key].results.competitors.map((rider) => (
                            <li key={rider.heat_rider_id}>
                              {rider.bib && `${rider.bib} · `}
                              {rider.name ?? "Rider"}
                              {rider.final != null && <span> · {rider.final}</span>}
                            </li>
                          ))}
                        </ol>
                      </section>
                    ),
                )}
              </div>
            )}
            {(state.standings?.length ?? 0) > 0 && (
              <section className="announcer-results">
                <h2>Division standings</h2>
                {state.areas?.enabled && <p>Across all areas · visible results</p>}
                <ol>
                  {state.standings?.map((rider) => (
                    <li key={`${rider.division_id}-${rider.heat_rider_id}`}>
                      {rider.position ?? "—"} · {rider.name ?? "Rider"} · {rider.final ?? "—"}
                      {rider.label && <small>{rider.label}</small>}
                    </li>
                  ))}
                </ol>
                <button disabled={!online || exporting} onClick={() => void downloadStandings()}>
                  {exporting ? "Preparing CSV…" : "Download standings CSV"}
                </button>
                {exportError && <p role="alert">{exportError}</p>}
              </section>
            )}
            {notes.length > 0 && (
              <section className="announcer-notes">
                <h2>Play-by-play notes</h2>
                <ol>
                  {notes.map((note) => (
                    <li key={note.id}>
                      <time dateTime={note.created_at}>
                        {new Intl.DateTimeFormat("en", {
                          timeZone: state.competition.timezone,
                          hour: "numeric",
                          minute: "2-digit",
                        }).format(new Date(note.created_at))}
                      </time>
                      <p>{note.text}</p>
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </>
        )}
      </div>
    </main>
  );
}
